import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Database, Server, Mail, AlertCircle, 
  CheckCircle2, RefreshCw, Clock, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth check hook for support pages
const useAuthCheck = (navigate) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('userInfo');
      
      if (!token || !userInfo) {
        toast.error('Please login to access the Support Dashboard');
        navigate('/login');
        return;
      }

      try {
        const user = JSON.parse(userInfo);
        if (user.role !== 'internal_team' && user.role !== 'admin') {
          toast.error('Access denied. Internal team role required.');
          navigate('/login');
          return;
        }
        setIsAuthorized(true);
      } catch (e) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  return { isAuthorized, isChecking };
};

export default function SystemHealth() {
  const navigate = useNavigate();
  const { isAuthorized, isChecking } = useAuthCheck(navigate);
  
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const token = localStorage.getItem('token');

  const fetchHealth = useCallback(async () => {
    if (!isAuthorized) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/support/health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHealth(data);
        setLastChecked(new Date());
      }
    } catch (error) {
      console.error('Error fetching health:', error);
      toast.error('Failed to fetch system health');
      setHealth({
        database: { status: 'error', error: 'Failed to connect' },
        assessmentEndpoint: { status: 'error' },
        emailService: { status: 'unknown' }
      });
    } finally {
      setLoading(false);
    }
  }, [token, isAuthorized]);

  useEffect(() => {
    if (!isAuthorized) return;
    
    fetchHealth();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth, isAuthorized]);

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Don't render if not authorized (will redirect)
  if (!isAuthorized) {
    return null;
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'healthy':
        return (
          <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Healthy
          </Badge>
        );
      case 'error':
        return (
          <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Error
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Unknown
          </Badge>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="system-health">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/support')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">System Health</h1>
              <p className="text-gray-600 mt-1">Monitor platform services and infrastructure</p>
            </div>
            
            <div className="text-right">
              <Button 
                variant="outline" 
                onClick={fetchHealth}
                disabled={loading}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {lastChecked && (
                <p className="text-xs text-gray-500 mt-2">
                  Last checked: {lastChecked.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {loading && !health ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Service Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Database Status */}
              <Card data-testid="db-status">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-lg ${health?.database?.status === 'healthy' ? 'bg-green-100' : 'bg-red-100'}`}>
                        <Database className={`w-6 h-6 ${health?.database?.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">Database</h3>
                        <p className="text-sm text-gray-500">PostgreSQL</p>
                      </div>
                    </div>
                    {getStatusBadge(health?.database?.status)}
                  </div>
                  
                  {health?.database?.latency && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-500">Latency</p>
                      <p className="text-2xl font-bold text-gray-900">{health.database.latency}</p>
                    </div>
                  )}
                  
                  {health?.database?.error && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-red-600">{health.database.error}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Assessment API Status */}
              <Card data-testid="api-status">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-lg ${health?.assessmentEndpoint?.status === 'healthy' ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Server className={`w-6 h-6 ${health?.assessmentEndpoint?.status === 'healthy' ? 'text-green-600' : 'text-gray-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">Assessment API</h3>
                        <p className="text-sm text-gray-500">FastAPI Backend</p>
                      </div>
                    </div>
                    {getStatusBadge(health?.assessmentEndpoint?.status)}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">Endpoint</p>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">/api/dynamic-assessments</code>
                  </div>
                </CardContent>
              </Card>

              {/* Email Service Status */}
              <Card data-testid="email-status">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-lg bg-gray-100">
                        <Mail className="w-6 h-6 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Email Service</h3>
                        <p className="text-sm text-gray-500">Resend API</p>
                      </div>
                    </div>
                    {getStatusBadge(health?.emailService?.status)}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      Email service status is not monitored in real-time
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Errors */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent Errors
                </CardTitle>
                <CardDescription>
                  Last 50 errors logged from the application
                </CardDescription>
              </CardHeader>
              <CardContent>
                {health?.recentErrors?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                    <p>No recent errors</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {health?.recentErrors?.map((error, index) => (
                      <div 
                        key={index}
                        className="p-3 bg-red-50 border border-red-100 rounded-lg"
                      >
                        <div className="flex items-start justify-between">
                          <p className="text-sm text-red-800 font-medium">
                            {error.message || 'Unknown error'}
                          </p>
                          <span className="text-xs text-red-600">
                            {error.time ? new Date(error.time).toLocaleString() : '-'}
                          </span>
                        </div>
                        {error.page && (
                          <p className="text-xs text-red-600 mt-1">
                            Page: {error.page}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card>
              <CardHeader>
                <CardTitle>Troubleshooting Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Unable to Load Assessment</h4>
                    <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                      <li>Check if corporate firewall is blocking *.emergent.host</li>
                      <li>Verify the assessment link is correct</li>
                      <li>Try clearing browser cache</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-medium text-yellow-900 mb-2">Progress Not Saving</h4>
                    <ul className="text-sm text-yellow-800 list-disc list-inside space-y-1">
                      <li>Check for network connectivity issues</li>
                      <li>Ensure assessment is not already completed</li>
                      <li>Verify API endpoint is reachable</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Email Not Received</h4>
                    <ul className="text-sm text-green-800 list-disc list-inside space-y-1">
                      <li>Check spam/junk folder</li>
                      <li>Verify email address in system</li>
                      <li>Use "Get Assessment Link" to share directly</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-900 mb-2">Missing from System</h4>
                    <ul className="text-sm text-purple-800 list-disc list-inside space-y-1">
                      <li>Upload assessment DOCX via Admin Dashboard</li>
                      <li>Verify employee name spelling</li>
                      <li>Check if uploaded under different region</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
